import { getAuthHeaders, handleAuthError } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

export interface DashboardApiFilters {
  companyId?: number | null;
  contactId?: number | null;
  contractId?: number | null;
  leadId?: number | null;
  pipelineId?: number | null;
  dealId?: number | null;
  proposalId?: number | null;
  assignedUserId?: number | null;
  assignedTeamId?: number | null;
  currencyCode?: string | null;
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  salesUserId?: number | null;
  salesUserRole?: string | null;
}

export interface FilterOption {
  value: number;
  label: string;
}

const FILTER_PARAMS: Record<keyof DashboardApiFilters, string> = {
  companyId: "company_id",
  contactId: "contact_id",
  contractId: "contract_id",
  leadId: "lead_id",
  pipelineId: "pipeline_id",
  dealId: "deal_id",
  proposalId: "proposal_id",
  assignedUserId: "assigned_user_id",
  assignedTeamId: "assigned_team_id",
  currencyCode: "currency_code",
  status: "status",
  startDate: "start_date",
  endDate: "end_date",
  salesUserId: "sales_user_id",
  salesUserRole: "sales_user_role",
};

export const buildDashboardQuery = (tenantId: string | number, filters: DashboardApiFilters = {}) => {
  const params = new URLSearchParams();
  params.append("tenant_id", String(tenantId));
  (Object.keys(FILTER_PARAMS) as (keyof DashboardApiFilters)[]).forEach((filterKey) => {
    const value = filters[filterKey];
    if (value !== undefined && value !== null && value !== "") {
      params.append(FILTER_PARAMS[filterKey], String(value));
    }
  });
  return params.toString();
};

export const fetchDashboardEndpoint = async (
  path: string,
  tenantId: string | number,
  filters: DashboardApiFilters,
  errorLabel: string
): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}${path}?${buildDashboardQuery(tenantId, filters)}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      handleAuthError({ response: { status: response.status } });
    }
    throw new Error(`Failed to load ${errorLabel} (${response.status})`);
  }
  return response.json();
};

export interface DashboardExportFile {
  downloadFileUrl: string;
  fileSize: number | null;
  fileExtension: string;
}

export const requestDashboardExport = async (
  path: string,
  tenantId: string | number,
  filters: DashboardApiFilters,
  errorLabel: string,
  extraParams: Record<string, string | null | undefined> = {}
): Promise<DashboardExportFile> => {
  const params = new URLSearchParams(buildDashboardQuery(tenantId, filters));
  Object.entries(extraParams).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  return requestExportByQuery(path, params.toString(), errorLabel);
};

export const requestExportByQuery = async (
  path: string,
  query: string,
  errorLabel: string
): Promise<DashboardExportFile> => {
  const response = await fetch(`${API_BASE_URL}${path}?${query}`, {
    headers: getAuthHeaders(),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.success || !payload?.download_file_url) {
    if (response.status === 401 || response.status === 403) {
      handleAuthError({ response: { status: response.status } });
    }
    const message = payload?.detail ?? payload?.error ?? payload?.message;
    throw new Error(message ? String(message) : `Failed to export ${errorLabel} (${response.status})`);
  }
  const fileSize = Number(payload.file_size);
  return {
    downloadFileUrl: String(payload.download_file_url),
    fileSize: Number.isFinite(fileSize) ? fileSize : null,
    fileExtension: String(payload.file_extension || "xlsx"),
  };
};

const triggerDownload = (href: string, fileName: string) => {
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const downloadExportFile = async (file: DashboardExportFile, fallbackName: string) => {
  const urlName = decodeURIComponent(file.downloadFileUrl.split("?")[0].split("/").pop() || "");
  const fileName = urlName || `${fallbackName}.${file.fileExtension}`;
  try {
    const response = await fetch(file.downloadFileUrl);
    if (!response.ok) throw new Error(`Download failed (${response.status})`);
    const objectUrl = URL.createObjectURL(await response.blob());
    triggerDownload(objectUrl, fileName);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch {
    triggerDownload(file.downloadFileUrl, fileName);
  }
};

export const CONTACT_PAGE_SIZE = 20;

const contactLabel = (contact: any) =>
  `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || `Contact #${contact.contact_id}`;

export const fetchContactPage = async (
  tenantId: string | number,
  companyId: number | null,
  page: number,
  search: string
): Promise<{ options: FilterOption[]; hasMore: boolean }> => {
  const params = new URLSearchParams({
    tenant_id: String(tenantId),
    page: String(page),
    page_size: String(CONTACT_PAGE_SIZE),
    response_mode: "light",
  });
  if (companyId) params.append("company_id", String(companyId));
  if (search.trim()) params.append("search", search.trim());
  const response = await fetch(`${API_BASE_URL}/contact/?${params.toString()}`, { headers: getAuthHeaders() });
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      handleAuthError({ response: { status: response.status } });
    }
    throw new Error(`Failed to load contacts (${response.status})`);
  }
  const data = await response.json();
  const results: any[] = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
  const hasMore = data && typeof data === "object" && "next" in data ? Boolean(data.next) : results.length === CONTACT_PAGE_SIZE;
  return {
    options: results.map((contact) => ({ value: contact.contact_id, label: contactLabel(contact) })),
    hasMore,
  };
};
